import _ from "lodash";
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
    PopulateReportController,
    GetScenariosController,
    GetCompanyController,
    GetReportController,
    UpdateVizByReportController,
    GetAccountingPlanByNameController
} from "@arch/presentation/controllers";
import {
    ScenarioEntity,
    CompanyEntity,
    ReportEntity
} from "@arch/domain/entity";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { DateUtils } from "@arch/_shared/utils/date.utils";
import { DesktopDreComponent } from "@app/views/auth/report/dre/desktop/desktop.component";
import { MobileDreComponent } from "@app/views/auth/report/dre/mobile/mobile.component";
import { DeviceService } from "@app/shared/services/device.service";
import {
    EventChangeComparationParams,
    EventChangePeriodParams
} from "../components/report-toolbar/report-toolbar.component";
import { LocalStoreService } from "@app/shared/services/local-store.service";
import { EventsService } from "@arch/domain/application/services/events.service";
import { ContactService } from "@app/shared/services/contact.service";
import { ReportPreferenceService } from "@arch/domain/application/services/report-preference.service";
import { AccountingPlanService } from "@app/shared/services/accounting-plan.service";

@Component({
    selector: "app-report-dre",
    imports: [
        SharedDsnModule,
        SharedMaterialModule,
        DesktopDreComponent,
        MobileDreComponent
    ],
    templateUrl: "./report-dre.component.html",
    styleUrl: "./report-dre.component.scss"
})
export class ReportDreComponent implements OnInit {
    @Input({ required: false }) public accordionState: string = null;
    @Input({ required: false }) public previewMode: boolean = false;
    @Output() public handleReport = new EventEmitter<EventEmitterReportType>();

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly deviceService = inject(DeviceService);
    private readonly contactService = inject(ContactService);
    private readonly getReportController = inject(GetReportController);
    private readonly getCompanyController = inject(GetCompanyController);
    private readonly populateReportController = inject(
        PopulateReportController
    );
    private readonly getScenariosController = inject(GetScenariosController);
    private readonly updateVizByReportController = inject(
        UpdateVizByReportController
    );
    private readonly ls = inject(LocalStoreService);
    private readonly eventsService = inject(EventsService);
    private readonly preferenceService = inject(ReportPreferenceService);
    private readonly accountingPlanService = inject(AccountingPlanService);
    private readonly getAccountingPlanByNameController = inject(
        GetAccountingPlanByNameController
    );
    public report: ReportEntity;
    public company: CompanyEntity;
    public scenarios: ScenarioEntity[];
    public loading: boolean;
    public isMobile = this.deviceService.getIsMobile();

    async ngOnInit(): Promise<void> {
        this.loading = true;
        this.handleReport.emit({
            loading: true,
            company: null,
            report: null
        });
        const { companyId, reportId } = this.route.snapshot.queryParams;
        if (!companyId || !reportId) {
            this.loading = false;
            this.router.navigate(["/"]);
            return;
        }
        const report = await this.getReportController.execute({
            reportId: reportId
        });
        const [company, scenarios] = await Promise.all([
            this.getCompanyController.execute({
                companyId: companyId,
                options: {
                    isGroup: report.props.isGroup
                }
            }),
            this.getScenariosController.execute({
                companyId,
                options: {
                    addOriginal: true,
                    addProjection: true
                }
            })
        ]);
        const accountingPlan =
            await this.getAccountingPlanByNameController.execute(
                company.props.accounting?.contract
            );
        if (accountingPlan) {
            this.accountingPlanService.init({ accountingPlan });
        }
        this.ls.setItem("company", company.props);
        if (!this.previewMode) {
            this.updateVizByReportController.execute({
                _id: report.props._id,
                contact: this.contactService.get()
            });
            this.eventsService.send(
                `Dash.${report.type}.${report.humanSourceName}.visualizar`
            );
        } else {
            this.eventsService.send(
                `Dash.${report.type}.${report.humanSourceName}.preview`
            );
        }
        report.setScenarioOptions(scenarios);
        this.report = report;
        this.preferenceService.setAll(report.props?.optionsContent);
        this.handleReport.emit({
            loading: false,
            report: this.report,
            company: company
        });
        company.setAnalysisPlusStatus(
            this.accountingPlanService.features["AnalysisPlus"]
        );
        company.setAnalysisPlusStatus(
            this.accountingPlanService.features["AnalysisPlus"]
        );
        this.company = company;
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: this.report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true,
                dre: true,
                balance: this.report.hasBalance
            }
        });
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onChangePeriod(event: EventChangePeriodParams): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.changeMainPeriod({
            period: event.periodName,
            startDate: event.startDate,
            scenario: event.scenario,
            endDate: event.endDate
        });
        _report.changeMainComparation({
            period: event.periodName,
            date: DateUtils.getPreviousMonthByPeriod(
                event.endDate,
                event.periodName
            ),
            scenario: event.scenario
        });
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: true,
                dre: true,
                balance: this.report.hasBalance
            }
        });
        this.report.changeMainPeriod({
            period: event.periodName,
            startDate: event.startDate,
            scenario: event.scenario,
            endDate: event.endDate
        });
        this.report.changeMainComparation({
            period: event.periodName,
            date: DateUtils.getPreviousMonthByPeriod(
                event.endDate,
                event.periodName
            ),
            scenario: event.scenario
        });
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onChangeComparation(
        event: EventChangeComparationParams
    ): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.activateComparation({
            scenario: event.scenario,
            period: event.periodName,
            baseDate: event.dateBase,
            comparationDate: event.comparationDate,
            allMonths: event.allMonthsCompare,
            dre: event.dre,
            ...(_report.hasBalance && {
                balance: event.balance
            })
        });
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true,
                dre: true,
                balance: this.report.hasBalance
            }
        });
        this.report.activateComparation({
            scenario: event.scenario,
            period: event.periodName,
            baseDate: event.dateBase,
            comparationDate: event.comparationDate,
            allMonths: event.allMonthsCompare,
            dre: event.dre,
            ...(this.report.hasBalance && {
                balance: event.balance
            })
        });
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }

    public async onResetPeriod(): Promise<void> {
        this.loading = true;
        const _report = _.cloneDeep(this.report);
        _report.clearComparation();
        const { notes, posts } = await this.populateReportController.execute({
            company: this.company,
            report: _report,
            options: {
                performance: true,
                drilldown: true,
                totalMonthsProjection: 12,
                benchmarking: this.company.isGroup ? false : true,
                dre: true,
                balance: this.report.hasBalance
            }
        });
        this.report.clearComparation();
        this.report.setNotes(notes);
        this.report.setPosts(posts);
        this.loading = false;
    }
}

type EventEmitterReportType = {
    loading: boolean;
    report: ReportEntity;
    company?: CompanyEntity;
};
